import { useEffect, useState } from 'react'
import type { Edge } from '@xyflow/react'
import { Button } from 'app/components/ui/button'
import { useListCopy } from 'app/hooks/useListCopy'
import { useUser } from 'app/utils'
import { NODE_ICON } from 'app/lib/workflows/palette'
import {
  contactSourceNoun,
  inferredTriggerLabel,
  triggerDisplayLabel,
  triggerIcon,
  triggerLabel,
} from 'app/lib/workflows/triggerCatalog'
import { Alert, type AlertTone } from './inspectors/Alert'
import { AvailableData } from './inspectors/AvailableData'
import { InfoHint } from './inspectors/fields'
import { Section } from './inspectors/Section'
import { SendPaperworkPanel } from './inspectors/SendPaperworkPanel'
import { InspectorShell } from './inspectors/InspectorShell'
import { TriggerTest, triggerTestType } from './inspectors/TriggerTest'
import {
  AddNotePanel,
  AssignOwnerPanel,
  EditObjectPanel,
  LogFollowUpPanel,
  TagObjectPanel,
  UpdateAppointmentPanel,
} from './inspectors/RecordPanels'
import { AddToPipelinePanel } from './inspectors/AddToPipelinePanel'
import { AiAgentSmsPanel, InitiateCallPanel, SendNotificationPanel, SendSmsPanel } from './inspectors/MessagePanels'
import { BranchPanel, ExitPanel, FilterPanel } from './inspectors/FlowPanels'
import { CreateTaskPanel, EditTaskPanel } from './inspectors/TaskPanels'
import { WaitForCallResultPanel, WaitForReplyPanel, WaitPanel, WaitUntilPanel } from './inspectors/WaitPanels'
import { WaitForKeywordPanel } from './inspectors/WaitForKeywordPanel'
import { type BackfillControl, TriggerPanel } from './inspectors/TriggerPanel'
import { type ObjectAvailability, availablePhoneFields, availableTargets } from 'app/lib/workflows/dataContext'
import type { ListCopy } from 'lib/listCopy'
import type { NodeIssues } from 'app/lib/workflows/nodeIssues'
import type { TriggerNodeData, WorkflowNode, WorkflowNodeData, WorkflowSettings } from 'app/types/workflows'
import type { PreviewSample } from './usePreviewSample'
import { actionNeed } from 'app/lib/workflows/validation'

type NodeInspectorProps = {
  node: WorkflowNode
  onChange: (id: string, patch: Record<string, unknown>) => void
  onClose: () => void
  /** Workflow-level texting policy, read back by SMS steps; `onEditSettings` opens the panel that edits it. */
  settings: WorkflowSettings
  onEditSettings: () => void
  availableFields: string[]
  contact: ObjectAvailability
  activity: ObjectAvailability
  issues: NodeIssues
  /** Workflow-level state, for the trigger's "Test this trigger" affordance. */
  active?: boolean
  saved?: boolean
  /** Unsaved changes — a live fire would run the last-saved version, so warn/block in the test. */
  dirty?: boolean
  /** The full graph, so the trigger test can dry-run a preview. */
  nodes?: WorkflowNode[]
  edges?: Edge[]
  /** Shared live sample record the builder previews against (fills message variables + data views). */
  preview?: PreviewSample
  /** Workflow-level trigger start date (ISO string). */
  triggerStartDate: string
  onTriggerStartDateChange: (value: string) => void
  backfill: BackfillControl
  /** Reopens the trigger picker in this same panel (the header's "Change" link on a trigger). */
  onChangeTrigger?: () => void
}

const kindLabels = (copy: ListCopy): Record<WorkflowNode[`type`], string> => ({
  trigger: `Trigger`,
  sendSms: `Send SMS`,
  aiAgentSms: `Hand off to AI agent`,
  initiateCall: `Initiate call`,
  createTask: `Create task`,
  editTask: `Edit task`,
  tagObject: `Tag`,
  addToPipeline: copy.addToList,
  editObject: `Edit`,
  updateAppointment: `Confirm appointment`,
  assignOwner: `Assign owner`,
  addNote: `Add note`,
  logFollowUp: `Log a follow-up`,
  sendPaperwork: `Send paperwork`,
  sendNotification: `Notify`,
  wait: `Wait`,
  waitUntil: `Wait until`,
  filter: `Filter`,
  waitForReply: `Wait for reply`,
  waitForKeyword: `Wait for a keyword`,
  waitForCallResult: `Wait for call result`,
  branch: `Branch`,
  exit: `Exit`,
})

type Status = { tone: AlertTone; lead?: string; text: string }

/** "What does this action act on, and does the trigger provide it?" Only speaks up when the answer
 *  is a problem: a green "needs a phone number, available" on every SMS step was noise at rest
 *  (design language rule 4, one status strip). Object-aware: for tag/edit it reflects the chosen
 *  target (contact vs activity). */
function objectRequirement(
  kind: WorkflowNode[`type`],
  data: WorkflowNodeData,
  contact: ObjectAvailability,
  activity: ObjectAvailability,
  availableFields: string[],
): Status | undefined {
  const need = actionNeed(kind, data)
  if (!need || need === `none`) return undefined
  if (need === `phone`) {
    if (availablePhoneFields(availableFields).length > 0) return undefined
    return { tone: `error`, lead: `Needs a phone number`, text: `This trigger provides no phone number.` }
  }
  const obj = need === `activity` ? `activity` : `contact`
  const availability = need === `activity` ? activity : contact
  if (availability === `guaranteed`) return undefined
  if (availability === `conditional`)
    return {
      tone: `warn`,
      lead: `Acts on the ${obj}`,
      text: `This trigger doesn't always have one, so the step is skipped when there's none. Turn on “Require a matched contact” on the trigger.`,
    }
  return {
    tone: `error`,
    lead: `Acts on the ${obj}`,
    text: `This trigger never provides ${obj === `activity` ? `an activity` : `a contact`}, so this step can't run.`,
  }
}

/** Right-hand configuration panel for the selected node. Thin shell that renders the
 *  per-kind panel and surfaces validation. */
export function NodeInspector({
  node,
  onChange,
  onClose,
  settings,
  onEditSettings,
  availableFields,
  contact,
  activity,
  issues,
  active,
  saved,
  dirty,
  nodes,
  edges,
  preview,
  triggerStartDate,
  onTriggerStartDateChange,
  backfill,
  onChangeTrigger,
}: NodeInspectorProps) {
  const set = (patch: Record<string, unknown>) => onChange(node.id, patch)
  const isSuper = useUser()?.type === `super`
  const copy = useListCopy()
  const [triggerTab, setTriggerTab] = useState<`configure` | `test`>(`configure`)
  // Reset to Configure whenever a different node is selected, so Test mode never lingers.
  useEffect(() => setTriggerTab(`configure`), [node.id])
  // A "Test" tab is only worth showing for a super-user on an activity-sourced trigger.
  const showTestTab = node.type === `trigger` && isSuper && !!triggerTestType(node.data)
  const { errors, warnings } = issues
  const targets = availableTargets(contact, activity)
  // One status strip per panel (design language rule 4): the most serious thing wins. Graph-level
  // errors (disconnected / missing upstream / needs backend) first, then the engine-gap note, then
  // loop risk, then the object requirement. The issue count lives in the toolbar badge, and
  // field-level keys render inside the panels next to their fields.
  const graphBanner = errors.unreachable ?? errors.upstream ?? errors.needsBackend ?? errors.agentNotReady
  const status: Status | undefined = graphBanner
    ? { tone: `error`, text: graphBanner }
    : warnings.engineGap
      ? { tone: `warn`, text: warnings.engineGap }
      : warnings.loopRisk
        ? { tone: `warn`, lead: `Can loop`, text: warnings.loopRisk }
        : warnings.pipelineMembership
          ? { tone: `warn`, lead: `Adds as well as moves`, text: warnings.pipelineMembership }
          : objectRequirement(node.type, node.data, contact, activity, availableFields)
  // The trigger's mark depends on which trigger was picked; every other block has a fixed one.
  const HeaderIcon = node.type === `trigger` ? triggerIcon((node.data as TriggerNodeData).kind) : NODE_ICON[node.type]
  // A trigger's name is inferred from what it is configured to do ("Broken appointment" only while the
  // appointment-broken condition is intact, else "Any appointment change"), unless a person typed one.
  // The underlying event is the subtitle when it differs, so the header matches the card.
  const triggerData = node.type === `trigger` ? (node.data as TriggerNodeData) : undefined
  const triggerEvent = triggerData ? triggerLabel(triggerData.kind) : `Settings`
  // Every other step is named like its card: the step's name as the title, the kind beneath it.
  const kindName =
    node.type === `addToPipeline` && node.data.operation === `move` ? copy.moveListStage : kindLabels(copy)[node.type]
  const inferredName = triggerData ? inferredTriggerLabel(triggerData, copy) : kindName
  const headerTitle = triggerData ? triggerDisplayLabel(triggerData, copy) : node.data.label || kindName
  // A typed name shows the inferred one beneath it, so what the step really is stays visible.
  const headerSubtitle =
    headerTitle !== inferredName
      ? inferredName
      : !triggerData
        ? `Settings`
        : headerTitle === triggerEvent
          ? `Trigger`
          : triggerEvent
  // Tag and Edit steps pick their record in their own "Applies to" section.
  const workflowTrigger = nodes?.find((candidate) => candidate.type === `trigger`)?.data as TriggerNodeData | undefined
  const contactSource =
    workflowTrigger &&
    actionNeed(node.type, node.data) === `contact` &&
    ![`tagObject`, `editObject`].includes(node.type)
      ? contactSourceNoun(workflowTrigger.kind)
      : undefined
  // Clearing the name hands back the inferred one, which the naming rule treats as not custom.
  const rename = (name: string) => set({ label: name || inferredName })

  return (
    <InspectorShell
      icon={HeaderIcon}
      title={headerTitle}
      subtitle={headerSubtitle}
      onRename={rename}
      renamePlaceholder={inferredName}
      action={node.type === `trigger` && onChangeTrigger ? { label: `Change`, onClick: onChangeTrigger } : undefined}
      onClose={onClose}
    >
      <>
        {status && (
          <div className="pb-4">
            <Alert tone={status.tone} lead={status.lead}>
              {status.text}
            </Alert>
          </div>
        )}

        {contactSource && (
          <Section title="Applies to">
            <div className="flex items-center text-sm text-default">
              The {contactSource}'s contact
              <InfoHint
                text={`The workflow runs for the contact this ${contactSource} belongs to. This step changes that contact, not the ${contactSource}.`}
              />
            </div>
          </Section>
        )}

        {node.type === `trigger` && (
          <>
            {showTestTab && (
              <div className="pb-4">
                <div className="flex gap-1.5">
                  {([`configure`, `test`] as const).map((tab) => (
                    <Button
                      key={tab}
                      variant="toggle"
                      size="xs"
                      aria-pressed={triggerTab === tab}
                      onClick={() => setTriggerTab(tab)}
                      className="flex-1 capitalize"
                    >
                      {tab}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {(!showTestTab || triggerTab === `configure`) && (
              <TriggerPanel
                data={node.data}
                set={set}
                errors={errors}
                triggerStartDate={triggerStartDate}
                onTriggerStartDateChange={onTriggerStartDateChange}
                backfill={backfill}
              />
            )}
            {showTestTab && triggerTab === `test` && (
              <TriggerTest data={node.data} active={active} saved={saved} dirty={dirty} nodes={nodes} edges={edges} />
            )}
          </>
        )}
        {node.type === `sendSms` && (
          <SendSmsPanel
            data={node.data}
            set={set}
            errors={errors}
            settings={settings}
            onEditSettings={onEditSettings}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `aiAgentSms` && (
          <AiAgentSmsPanel
            data={node.data}
            set={set}
            errors={errors}
            settings={settings}
            onEditSettings={onEditSettings}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `initiateCall` && (
          <InitiateCallPanel
            data={node.data}
            set={set}
            errors={errors}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `createTask` && (
          <CreateTaskPanel
            data={node.data}
            set={set}
            errors={errors}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `editTask` && (
          <EditTaskPanel
            data={node.data}
            set={set}
            errors={errors}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `tagObject` && <TagObjectPanel data={node.data} set={set} errors={errors} targets={targets} />}
        {node.type === `addToPipeline` && <AddToPipelinePanel data={node.data} set={set} errors={errors} />}
        {node.type === `editObject` && (
          <EditObjectPanel
            data={node.data}
            set={set}
            errors={errors}
            targets={targets}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `assignOwner` && <AssignOwnerPanel data={node.data} set={set} errors={errors} />}
        {node.type === `addNote` && (
          <AddNotePanel
            data={node.data}
            set={set}
            errors={errors}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `logFollowUp` && (
          <LogFollowUpPanel
            data={node.data}
            set={set}
            errors={errors}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `sendPaperwork` && (
          <SendPaperworkPanel
            data={node.data}
            set={set}
            errors={errors}
            settings={settings}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `sendNotification` && (
          <SendNotificationPanel
            data={node.data}
            set={set}
            errors={errors}
            availableFields={availableFields}
            sample={preview?.sample}
          />
        )}
        {node.type === `updateAppointment` && <UpdateAppointmentPanel data={node.data} set={set} errors={errors} />}
        {node.type === `wait` && <WaitPanel data={node.data} set={set} errors={errors} />}
        {node.type === `waitUntil` && (
          <WaitUntilPanel data={node.data} set={set} errors={errors} availableFields={availableFields} />
        )}
        {node.type === `filter` && (
          <FilterPanel data={node.data} set={set} errors={errors} availableFields={availableFields} />
        )}
        {node.type === `waitForReply` && <WaitForReplyPanel key={node.id} data={node.data} set={set} errors={errors} />}
        {node.type === `waitForKeyword` && (
          <WaitForKeywordPanel key={node.id} data={node.data} set={set} errors={errors} />
        )}
        {node.type === `waitForCallResult` && <WaitForCallResultPanel />}
        {node.type === `branch` && (
          <BranchPanel data={node.data} set={set} errors={errors} availableFields={availableFields} />
        )}
        {node.type === `exit` && <ExitPanel />}

        {/* What data is in scope at this step — the trigger has its own "available to later steps" view. */}
        {node.type !== `trigger` && node.type !== `exit` && (
          <div className="pt-4">
            <AvailableData fields={availableFields} title="Data available here" preview={preview} />
          </div>
        )}
      </>
    </InspectorShell>
  )
}
